import { Navigate } from 'react-router-dom'
import { useAuthStore } from '@/shared/store/authStore'

interface RoleGuardProps {
  /** Roles that are allowed to access this route. If null/undefined, all authenticated users can access. */
  allowedRoles?: string[] | null
  /**
   * Authorities that DENY the route even when `allowedRoles` admits them — checked
   * first, so it wins.
   *
   * Added 2026-09-08 alongside `NavItem.deniedRoles`, for one server sentence the
   * positive list cannot say: `GET /clients` is
   * `isAuthenticated() and !hasAuthority('SCOPE_ASSIGNED_ONLY')`. Naming CREW without
   * this hands a scoped member a page whose first request 403s; omitting CREW hides a
   * list the server serves to every unscoped one.
   *
   * Use it ONLY to mirror a `!hasAuthority(...)` the backend already writes. A denial
   * with no server behind it is a UI gate stricter than its boundary — the failure
   * mode that produces no 403, no console error and no failing test.
   */
  deniedRoles?: string[] | null
  children: React.ReactNode
}

// Roles in the store may be prefixed with "ROLE_" (Spring Security format) while the
// router's lists use unprefixed names. Normalize both. The two ADR-036 switches carry
// no prefix on either side, so they survive this untouched.
const normalize = (r: string) => r.replace(/^ROLE_/, '')

export function RoleGuard({ allowedRoles, deniedRoles, children }: RoleGuardProps) {
  const teamMember = useAuthStore((s) => s.teamMember)
  const userRoles = teamMember?.roles ?? []

  if (deniedRoles && deniedRoles.length > 0) {
    const denied = new Set(deniedRoles.map(normalize))
    if (userRoles.some((r) => denied.has(normalize(r)))) {
      return <Navigate to="/" replace />
    }
  }

  // No role restriction — any authenticated user can access
  if (!allowedRoles) {
    return <>{children}</>
  }

  const allowed = new Set(allowedRoles.map(normalize))
  const hasAccess = userRoles.some((r) => allowed.has(normalize(r)))

  if (!hasAccess) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}
